<template>
  <div class="company-account-list-container company-account-list-main company-account-list-page--glass glass-tab-page">
    <BrucdcumHeader :breadcrumb="['Company', 'Account List']" />

    <div class="glass-tab-view glass-tab-view--flush company-account-list-glass-shell">
      <div class="glass-tab-view__content company-account-list-tab-content company-account-list-page-stack">
        <div class="company-account-list-header-block">
          <div class="company-account-list-header-row">
            <div class="company-account-list-header-row__text">
              <h2>Accounts List</h2>
              <p class="company-account-list-header-row__sub">
                View, filter, and manage your company chart of accounts
              </p>
            </div>
            <div class="company-account-list-header-actions">
              <SecondaryButton
                v-if="showLoadChartButton"
                :label="loadChartButtonLabel"
                icon="bi bi-cloud-download"
                custom-class="company-account-list-load-chart-btn"
                :loading="isLoadingChart"
                :disabled="isLoadingChart"
                @click="loadChartOfAccounts"
              />
              <SecondaryButton label="Export to CSV" icon="bi bi-download" />
              <PrimaryButton
                label="Add New Account"
                icon="bi bi-plus-circle"
                @click="navigateToAddAccount"
              />
            </div>
          </div>
        </div>

        <div class="company-account-list-filters company-account-list-filters-toolbar-row">
          <div class="company-account-list-filters-left">
            <LabelDropdown
              label="Search Account"
              select-id="company-account-search"
              v-model="selectedAccountFilter"
              inline
              searchable
              search-placeholder="Search accounts..."
              custom-class="company-account-list-filter-dropdown company-account-list-filter-dropdown--search"
              :options="accountSearchFilterOptions"
              :disabled="loading"
            />
            <LabelDropdown
              label="Account Type"
              select-id="company-account-type"
              v-model="selectedAccountType"
              inline
              :searchable="uniqueAccountTypes.length >= 4"
              custom-class="company-account-list-filter-dropdown company-account-list-filter-dropdown--type"
              :options="accountTypeFilterOptions"
            />
            <LabelDropdown
              label="Current View"
              select-id="company-account-view"
              v-model="currentViewStatus"
              inline
              :searchable="false"
              custom-class="company-account-list-filter-dropdown company-account-list-filter-dropdown--view"
              :options="currentViewFilterOptions"
            />
          </div>
        </div>

        <div class="company-account-list-glass-panel company-account-list-main-card">
          <div class="consultant-glass-table-section company-account-list-table-section">
            <div
              class="consultant-glass-table-responsive consultant-glass-table-responsive--framed"
              :class="{ 'consultant-glass-table-responsive--loading': loading }"
            >
              <div class="consultant-glass-table-scroll-x">
                <table class="consultant-glass-table company-account-list-table">
                  <thead>
                    <tr>
                      <th>Active</th>
                      <th>No</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Balance</th>
                      <th class="company-account-list-actions-header company-account-list-col-actions">Register</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="loading">
                      <td colspan="6" class="consultant-glass-table-no-records company-account-list-no-records">
                        Loading...
                      </td>
                    </tr>
                    <tr v-else-if="error">
                      <td colspan="6" class="consultant-glass-table-no-records company-account-list-no-records">
                        {{ error }}
                      </td>
                    </tr>
                    <tr v-else-if="totalFilteredAccounts === 0">
                      <td colspan="6" class="consultant-glass-table-no-records company-account-list-no-records">
                        No records to show
                      </td>
                    </tr>
                    <template v-else>
                      <tr
                        v-for="(account, idx) in paginatedAccounts"
                        :key="account.id || account.no"
                        class="consultant-glass-table-row company-account-list-row"
                        :style="{ '--cal-row-delay': `${0.04 + Math.min(idx, 12) * 0.03}s` }"
                      >
                        <td>
                          <input type="checkbox" v-model="account.active" disabled />
                        </td>
                        <td>{{ account.no || account.accountNumber }}</td>
                        <td>{{ account.name || account.accountName }}</td>
                        <td>{{ account.type || account.accountType }}</td>
                        <td>
                          {{
                            account.balance != null
                              ? account.balance
                              : account.currentBalance != null
                                ? '$' + Number(account.currentBalance).toFixed(2)
                                : '-'
                          }}
                        </td>
                        <td class="company-account-list-actions-cell company-account-list-col-actions">
                          <div class="company-account-list-action-buttons">
                            <SecondaryButton
                              label=""
                              icon="bi bi-journal-bookmark"
                              custom-class="company-account-list-action-btn"
                              aria-label="Register"
                              title="Register"
                            />
                            <SecondaryButton
                              label=""
                              icon="bi bi-eye"
                              custom-class="company-account-list-action-btn"
                              aria-label="Preview"
                              title="Preview"
                              @click="previewAccount(account)"
                            />
                            <SecondaryButton
                              label=""
                              icon="bi bi-pencil"
                              custom-class="company-account-list-action-btn"
                              aria-label="Edit"
                              title="Edit"
                              @click="editAccount(account)"
                            />
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="company-account-list-pagination-wrapper">
              <PaginationComponent
                :current-page="currentPage"
                :total-items="totalFilteredAccounts"
                :items-per-page="itemsPerPage"
                item-label="accounts"
                @page-change="goToPage"
                @items-per-page-change="updateItemsPerPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Account Preview Modal -->
    <Transition name="account-modal-overlay">
      <div
        v-if="showPreviewModal"
        class="account-preview-modal-overlay account-preview-modal-overlay--glass"
        @click.self="closePreviewModal"
      >
        <Transition name="account-modal-panel" appear>
          <div
            v-if="showPreviewModal"
            class="account-preview-modal account-preview-modal--glass"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-preview-modal-title"
            @click.stop
          >
            <div class="account-preview-modal-header account-preview-modal-header--glass">
              <h3 id="account-preview-modal-title">Account Details</h3>
              <button
                type="button"
                class="account-preview-modal-close-btn"
                aria-label="Close"
                @click="closePreviewModal"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div v-if="selectedAccount" class="account-preview-modal-body account-preview-modal-body--glass">
              <div class="account-preview-row">
                <div class="account-preview-label">Account Number:</div>
                <div class="account-preview-value">
                  {{ selectedAccount.no || selectedAccount.accountNumber || '-' }}
                </div>
              </div>
              <div class="account-preview-row">
                <div class="account-preview-label">Account Name:</div>
                <div class="account-preview-value">
                  {{ selectedAccount.name || selectedAccount.accountName || '-' }}
                </div>
              </div>
              <div class="account-preview-row">
                <div class="account-preview-label">Account Type:</div>
                <div class="account-preview-value">
                  {{ selectedAccount.type || selectedAccount.accountType || '-' }}
                </div>
              </div>
              <div class="account-preview-row">
                <div class="account-preview-label">Current Balance:</div>
                <div class="account-preview-value">
                  {{
                    selectedAccount.balance != null
                      ? '$' + Number(selectedAccount.balance).toFixed(2)
                      : selectedAccount.currentBalance != null
                        ? '$' + Number(selectedAccount.currentBalance).toFixed(2)
                        : '-'
                  }}
                </div>
              </div>
              <div class="account-preview-row">
                <div class="account-preview-label">Status:</div>
                <div class="account-preview-value">
                  <span :class="selectedAccount.active ? 'account-status-active' : 'account-status-inactive'">
                    {{ selectedAccount.active ? 'Active' : 'Inactive' }}
                  </span>
                </div>
              </div>
              <div v-if="selectedAccount.description" class="account-preview-row">
                <div class="account-preview-label">Description:</div>
                <div class="account-preview-value">{{ selectedAccount.description }}</div>
              </div>
              <div v-if="selectedAccount.createdAt" class="account-preview-row">
                <div class="account-preview-label">Created Date:</div>
                <div class="account-preview-value">{{ formatDate(selectedAccount.createdAt) }}</div>
              </div>
              <div v-if="selectedAccount.updatedAt" class="account-preview-row">
                <div class="account-preview-label">Last Updated:</div>
                <div class="account-preview-value">{{ formatDate(selectedAccount.updatedAt) }}</div>
              </div>
            </div>
            <div class="account-preview-modal-footer account-preview-modal-footer--glass">
              <SecondaryButton label="Close" @click="closePreviewModal" />
              <PrimaryButton label="Edit Account" icon="bi bi-pencil" @click="editSelectedAccount" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- Confirmation Modal -->
    <Transition name="account-modal-overlay">
      <div
        v-if="showConfirmModal"
        class="account-confirm-modal-overlay account-confirm-modal-overlay--glass"
        @click.self="closeConfirmModal"
      >
        <Transition name="account-modal-panel" appear>
          <div
            v-if="showConfirmModal"
            class="account-confirm-modal account-confirm-modal--glass"
            role="dialog"
            aria-modal="true"
            aria-labelledby="account-confirm-modal-title"
            @click.stop
          >
            <div class="account-confirm-modal-header account-confirm-modal-header--glass">
              <h3 id="account-confirm-modal-title">Confirm Action</h3>
              <button
                type="button"
                class="account-confirm-modal-close-btn"
                aria-label="Close"
                @click="closeConfirmModal"
              >
                <i class="bi bi-x-lg" aria-hidden="true"></i>
              </button>
            </div>
            <div class="account-confirm-modal-body account-confirm-modal-body--glass">
              <p>{{ confirmMessage }}</p>
            </div>
            <div class="account-confirm-modal-footer account-confirm-modal-footer--glass">
              <SecondaryButton label="Cancel" @click="closeConfirmModal" />
              <PrimaryButton
                label="Confirm"
                custom-class="company-account-list-confirm-btn"
                @click="executeConfirmAction"
              />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      button-text="OK"
      @close="closeSuccessModal"
    />

    <ErrorModal
      :show="showErrorModal"
      :title="errorTitle"
      :message="errorMessage"
      button-text="OK"
      @close="closeErrorModal"
    />

    <ProgressTracker
      :show="showProgressTracker"
      title="Loading Chart of Accounts"
      :total-count="progressTrackerData.totalCount"
      :processed-count="progressTrackerData.processedCount"
      :completed-count="progressTrackerData.completedCount"
      :skipped-count="progressTrackerData.skippedCount"
      :processing-count="progressTrackerData.processingCount"
      :error-count="progressTrackerData.errorCount"
      :errors="progressTrackerData.errors"
      :is-processing="isProgressProcessing"
      item-label="accounts"
      @close="handleProgressTrackerClose"
    />
  </div>
</template>

<script>
import '../styles/CompanyAccountList.css';
import ApiService from '@/services/api.service.js';
import logger from '@/utils/logger';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import SuccessModal from '@/components/SuccessModal.vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import ProgressTracker from '@/components/ProgressTracker.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import ChartOfAccountsService from '../services/chartOfAccounts.service.js';

export default {
  name: 'CompanyAccountList',
  components: {
    BrucdcumHeader,
    ErrorModal,
    SuccessModal,
    PaginationComponent,
    ProgressTracker,
    PrimaryButton,
    SecondaryButton,
    LabelDropdown,
  },
  data() {
    return {
      accounts: [],
      loading: false,
      error: '',
      currentViewStatus: 'active',
      selectedAccountFilter: '',
      selectedAccountType: '',
      showPreviewModal: false,
      selectedAccount: null,
      isLoadingChart: false,
      currentPage: 1,
      itemsPerPage: 10,
      showConfirmModal: false,
      confirmMessage: '',
      confirmAction: null,
      showSuccessModal: false,
      successTitle: 'Success',
      successMessage: '',
      showErrorModal: false,
      errorTitle: 'Error',
      errorMessage: '',
      showProgressTracker: false,
      progressTrackerData: {
        totalCount: 0,
        processedCount: 0,
        completedCount: 0,
        skippedCount: 0,
        processingCount: 0,
        errorCount: 0,
        errors: [],
      },
      isProgressProcessing: false,
    };
  },
  async created() {
    await this.loadAccounts();
  },
  methods: {
    async loadChartOfAccounts() {
      if (this.isLoadingChart) return;

      const companyId = this.$route.params.id;
      if (!companyId) {
        this.showError('Error', 'Company ID not found');
        return;
      }

      this.showConfirmModal = true;
      const missing = this.missingChartAccountCount;
      const totalPredefined = ChartOfAccountsService.getTotalAccountCount();
      if (missing === totalPredefined) {
        this.confirmMessage = `This will create ${missing} predefined chart of accounts for your company. Existing account numbers are skipped. Do you want to continue?`;
      } else {
        this.confirmMessage = `This will create ${missing} missing predefined account${missing === 1 ? '' : 's'} (${totalPredefined - missing} already present). Duplicates are skipped. Do you want to continue?`;
      }
      this.confirmAction = this.executeLoadChartOfAccounts;
    },
    async executeLoadChartOfAccounts() {
      this.closeConfirmModal();
      this.isLoadingChart = true;

      try {
        const companyId = this.$route.params.id;
        const totalAccounts = ChartOfAccountsService.getTotalAccountCount();

        this.progressTrackerData = {
          totalCount: totalAccounts,
          processedCount: 0,
          completedCount: 0,
          skippedCount: 0,
          processingCount: totalAccounts,
          errorCount: 0,
          errors: [],
        };
        this.showProgressTracker = true;
        this.isProgressProcessing = true;

        const result = await ChartOfAccountsService.createChartOfAccounts(companyId, (progress) => {
          this.progressTrackerData = { ...progress };
        });

        this.isProgressProcessing = false;

        if (result.errorCount === 0 && result.skippedCount === 0) {
          this.showSuccess('Success', `Successfully created ${result.successCount} accounts!`);
        } else if (result.errorCount === 0) {
          this.showSuccess(
            'Success',
            `Created ${result.successCount} accounts successfully. ${result.skippedCount} duplicate accounts were skipped.`
          );
        } else {
          this.showSuccess(
            'Partial Success',
            `Created ${result.successCount} accounts successfully. ${result.skippedCount} duplicates skipped. ${result.errorCount} accounts failed to create.`
          );
        }

        await this.loadAccounts();
      } catch (error) {
        logger.error('Error loading chart of accounts:', error);
        this.showError('Error', error.message || 'Failed to load chart of accounts. Please try again.');
        this.isProgressProcessing = false;
      } finally {
        this.isLoadingChart = false;
      }
    },
    handleProgressTrackerClose() {
      this.showProgressTracker = false;
      this.progressTrackerData = {
        totalCount: 0,
        processedCount: 0,
        completedCount: 0,
        skippedCount: 0,
        processingCount: 0,
        errorCount: 0,
        errors: [],
      };
    },
    async loadAccounts() {
      this.loading = true;
      this.error = '';
      try {
        const companyId = this.$route.params.id;
        const res = await ApiService.getCompanyAccounts(companyId);
        this.accounts = res.data.accounts || res.data || [];
      } catch (err) {
        this.error = 'Failed to load accounts';
        this.accounts = [];
      } finally {
        this.loading = false;
      }
    },
    navigateToAddAccount() {
      const companyId = this.$route.params.id;
      this.$router.push(`/company/${companyId}/dashboard/new-account`);
    },
    editAccount(account) {
      logger.log('Edit account:', account);
      const companyId = this.$route.params.id;
      const accountId = account.id || account.accountNumber;
      if (accountId) {
        this.$router.push(`/company/${companyId}/dashboard/edit-account/${accountId}`);
      } else {
        this.showError('Error', 'No valid account ID or account number found for editing.');
      }
    },
    previewAccount(account) {
      this.selectedAccount = account;
      this.showPreviewModal = true;
    },
    closePreviewModal() {
      this.showPreviewModal = false;
      this.selectedAccount = null;
    },
    editSelectedAccount() {
      if (this.selectedAccount) {
        this.editAccount(this.selectedAccount);
        this.closePreviewModal();
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    closeConfirmModal() {
      this.showConfirmModal = false;
      this.confirmMessage = '';
      this.confirmAction = null;
    },
    executeConfirmAction() {
      if (this.confirmAction) {
        this.confirmAction();
      }
    },
    showSuccess(title, message) {
      this.successTitle = title;
      this.successMessage = message;
      this.showSuccessModal = true;
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successTitle = 'Success';
      this.successMessage = '';
    },
    showError(title, message) {
      this.errorTitle = title;
      this.errorMessage = message;
      this.showErrorModal = true;
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorTitle = 'Error';
      this.errorMessage = '';
    },
    goToPage(page) {
      this.currentPage = page;
    },
    updateItemsPerPage(newItemsPerPage) {
      this.itemsPerPage = newItemsPerPage;
      this.currentPage = 1;
    },
  },
  computed: {
    missingChartAccountCount() {
      return ChartOfAccountsService.getMissingChartAccountCount(this.accounts);
    },
    showLoadChartButton() {
      return this.missingChartAccountCount > 0;
    },
    loadChartButtonLabel() {
      if (this.isLoadingChart) return 'Loading Chart...';
      const missing = this.missingChartAccountCount;
      const total = ChartOfAccountsService.getTotalAccountCount();
      if (missing === total) return 'Load Chart of Accounts';
      return `Load Remaining (${missing})`;
    },
    accountSearchFilterOptions() {
      return [
        { value: '', label: 'All Accounts' },
        ...this.accounts.map((account) => ({
          value: account.id,
          label: `${account.no || account.accountNumber} - ${account.name || account.accountName}`,
        })),
      ];
    },
    uniqueAccountTypes() {
      const types = new Set();
      this.accounts.forEach((account) => {
        const type = account.type || account.accountType;
        if (type) {
          types.add(type);
        }
      });
      return Array.from(types).sort();
    },
    accountTypeFilterOptions() {
      return [
        { value: '', label: 'All Types' },
        ...this.uniqueAccountTypes.map((type) => ({ value: type, label: type })),
      ];
    },
    currentViewFilterOptions() {
      return [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' },
      ];
    },
    filteredAccounts() {
      let filtered = this.accounts.filter(
        (a) => (a.active !== undefined ? (a.active ? 'active' : 'inactive') : 'active') === this.currentViewStatus
      );

      if (this.selectedAccountType) {
        filtered = filtered.filter((account) => {
          const accountType = account.type || account.accountType;
          return accountType === this.selectedAccountType;
        });
      }

      if (this.selectedAccountFilter !== '' && this.selectedAccountFilter != null) {
        filtered = filtered.filter(
          (account) => String(account.id) === String(this.selectedAccountFilter)
        );
      }

      return filtered;
    },
    totalFilteredAccounts() {
      return this.filteredAccounts.length;
    },
    paginatedAccounts() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredAccounts.slice(start, end);
    },
  },
  watch: {
    currentViewStatus() {
      this.currentPage = 1;
    },
    selectedAccountFilter() {
      this.currentPage = 1;
    },
    selectedAccountType() {
      this.currentPage = 1;
    },
  },
};
</script>
